/**
 * Notification Bell - Web Component Style
 *
 * Entry point for the Web Component style implementation.
 */

import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { NotificationInboxWebComponent } from "./notification-inbox-web-component";
import { useState, useRef } from "react";
import { useNotificationCount } from "@/contexts/NotificationCountContext";
import { useAppSelector } from "@/store/hooks"
import { useTranslation } from 'react-i18next';

export function NotificationBellWebComponent() {
    const { t } = useTranslation('inbox')
    const [open, setOpen] = useState(false);
    const { unreadCount, isLoading } = useNotificationCount();

    // 🔥 检查用户是否已登录（Redux store 中是否有 userId）
    const userId = useAppSelector((state) => state.user.userId);

    // 公告并进了收件箱的第三个页签(R-044)，顶栏不再有独立的 Megaphone。铃铛上的
    // 数字仍然只代表 Novu 未读(公告已读是另一套口径，混在一起数会对不上"标记
    // 全部已读"的结果)，所以待处理公告只在没有数字可显示时挂一个不带数字的小红
    // 点 —— 有数字时用户本来就会点开，进去就看见页签上的点。
    const hasPendingAnnouncements = useAppSelector((state) => state.announcements.items.length > 0);

    // Track if inbox has been opened at least once.
    // ⚠️ All hooks must be called unconditionally before any early return.
    const hasOpenedRef = useRef(false);
    if (open && !hasOpenedRef.current) {
        hasOpenedRef.current = true;
    }

    // 如果用户未登录，不渲染通知铃铛（在所有 hooks 调用之后再 early return）
    if (!userId) {
        return null;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent relative"
                    title={t('notifications.title')}
                >
                    <Bell className="h-4 w-4" />
                    {!isLoading && unreadCount === 0 && hasPendingAnnouncements && (
                        <span
                            data-testid="notification-bell-announcement-dot"
                            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-destructive"
                        />
                    )}
                    {!isLoading && unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-xs text-destructive-foreground flex items-center justify-center font-medium">
                            {unreadCount > 99 ? "..." : unreadCount}
                        </span>
                    )}
                    <span className="sr-only">{t('notifications.title')}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto p-0 border-border shadow-lg"
                align="end"
                sideOffset={8}
            >
                {/* Keep component mounted after first open */}
                {hasOpenedRef.current && (
                    <div className={open ? "" : "hidden"}>
                        <NotificationInboxWebComponent isOpen={open} onRequestClose={() => setOpen(false)} />
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
